import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Row, Statistic, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Area } from '@ant-design/charts';
import { adminApi } from '@services/api';
import { useAutoRefresh } from '@hooks/useAutoRefresh';

const { Text } = Typography;

interface Bucket {
  minuteStart: string;
  /** null means the bucket is gone — render as a gap, never as zero. */
  activeUsers: number | null;
  requests: number | null;
  /** The server flags the newest minutes (still being counted) as partial. */
  partial: boolean;
}

interface ActiveUsersResponse {
  windowMinutes: number;
  approximate: boolean;
  errorMarginPercent: number;
  lastComplete: Bucket | null;
  peak: Bucket | null;
  series: Bucket[];
}

type Segment = 'complete' | 'in-progress';

interface ChartPoint {
  /** The bucket's ISO minuteStart. Unique per minute; labels format it to IST. */
  minute: string;
  users: number | null;
  segment: Segment;
  partial: boolean;
}

const LINE_COLOR = '#1677ff';

/**
 * HH:mm in IST whatever the viewer's browser time zone is — the rest of the
 * admin reports in IST, so a UTC or local-time axis here would disagree with
 * every other card.
 */
const hhmm = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Kolkata',
  });

const formatUsers = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : n.toLocaleString('en-IN');

const isInProgress = (series: ChartPoint[]) => series[0]?.segment === 'in-progress';

/**
 * Split the series into the complete line and the in-progress tail.
 *
 * Null minutes stay in the data: G2 breaks an area or line at a null y
 * (`connect` defaults to false in both shapes), which is the gap we want.
 * Filtering them out would make the line bridge straight across the hole.
 *
 * The in-progress segment starts on the last complete minute so the two
 * parts join rather than leaving a hole that reads as missing data. If that
 * minute is itself null the tail stays detached — a gap is still a gap.
 */
const buildChartData = (series: Bucket[]): ChartPoint[] => {
  const complete = series.filter((b) => !b.partial);
  const partial = series.filter((b) => b.partial);
  const points: ChartPoint[] = complete.map((b) => ({
    minute: b.minuteStart,
    users: b.activeUsers,
    segment: 'complete',
    partial: false,
  }));
  if (partial.length > 0) {
    const last = complete[complete.length - 1];
    if (last && last.activeUsers !== null) {
      points.push({
        minute: last.minuteStart,
        users: last.activeUsers,
        segment: 'in-progress',
        partial: false,
      });
    }
    partial.forEach((b) =>
      points.push({
        minute: b.minuteStart,
        users: b.activeUsers,
        segment: 'in-progress',
        partial: true,
      })
    );
  }
  return points;
};

/**
 * Live API-active users per minute.
 *
 * Two rules this card exists to honour, both of which decide whether anyone
 * trusts the number:
 *
 *   The headline reads `lastComplete`, never the in-progress minute. The
 *   current minute is still filling, so driving the statistic off it makes the
 *   card appear to collapse every 60 seconds and that gets reported as an
 *   outage. The in-progress minutes are drawn dashed at half opacity for the
 *   same reason.
 *
 *   A null bucket is a gap, not a zero. The server returns null when a minute
 *   has expired or been evicted; plotting it as 0 invents a trough that looks
 *   like real data, and bridging it hides that the minute is missing.
 */
export const ActiveUsersCard: React.FC<{ autoRefresh?: boolean }> = ({ autoRefresh = false }) => {
  const [data, setData] = useState<ActiveUsersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await adminApi.getActiveUsersSeries({ minutes: 60 });
      setData(res.data);
      setError(null);
    } catch {
      setError('Could not load active users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  useAutoRefresh(fetchData, autoRefresh, 15_000);

  const series = useMemo(() => data?.series ?? [], [data]);
  const chartData = useMemo(() => buildChartData(series), [series]);
  // One slot per minute in server order, so a null minute keeps its place on
  // the axis instead of collapsing out of it.
  const minutes = useMemo(() => series.map((b) => b.minuteStart), [series]);

  const gaps = series.filter((b) => !b.partial && b.activeUsers === null).length;
  const inProgress = series.filter((b) => b.partial).length;

  const lastComplete = data?.lastComplete ?? null;
  const peak = data?.peak ?? null;

  return (
    <Card
      loading={loading}
      title={
        <span>
          Active Users / min (API){' '}
          <Tooltip
            title={
              'Distinct users who called the API in each minute. This is not true CCU — ' +
              'a player watching a client-side match sim makes no API calls and is invisible, ' +
              `so it reads below real concurrency. Approximate, ±${data?.errorMarginPercent ?? 0.81}%. ` +
              'Times are IST.'
            }
          >
            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
          </Tooltip>
        </span>
      }
    >
      {error ? (
        <Alert type="warning" message={error} showIcon />
      ) : (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Statistic
                title={
                  lastComplete
                    ? `Last complete minute (${hhmm(lastComplete.minuteStart)} IST)`
                    : 'Last complete minute'
                }
                value={formatUsers(lastComplete?.activeUsers)}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title={peak ? `Peak this hour (${hhmm(peak.minuteStart)} IST)` : 'Peak this hour'}
                value={formatUsers(peak?.activeUsers)}
              />
            </Col>
          </Row>

          <Area
            data={chartData}
            xField="minute"
            yField="users"
            seriesField="segment"
            height={200}
            scale={{ x: { domain: minutes } }}
            axis={{ x: { labelAutoHide: true, labelFormatter: (v: string) => hhmm(v) } }}
            style={{
              fill: LINE_COLOR,
              fillOpacity: (d: ChartPoint[]) => (isInProgress(d) ? 0.125 : 0.25),
              // Explicit although it is G2's default: a null minute breaks
              // the area instead of being bridged.
              connect: false,
            }}
            line={{
              style: {
                stroke: LINE_COLOR,
                lineWidth: 2,
                strokeOpacity: (d: ChartPoint[]) => (isInProgress(d) ? 0.5 : 1),
                lineDash: (d: ChartPoint[]) => (isInProgress(d) ? [4, 4] : []),
                connect: false,
              },
            }}
            tooltip={{
              title: (d: ChartPoint) => `${hhmm(d.minute)} IST`,
              // The joining point appears in both segments with the same
              // name, value and colour, so G2 shows it once.
              items: [
                (d: ChartPoint) => ({
                  name: d.partial ? 'Active users (still counting)' : 'Active users',
                  value: d.users === null ? 'unavailable' : d.users.toLocaleString('en-IN'),
                }),
              ],
            }}
          />

          <Text type="secondary" style={{ fontSize: 12 }}>
            Times are IST.
            {inProgress > 0
              ? ` The last ${inProgress === 1 ? 'minute is' : `${inProgress} minutes are`} still being counted — drawn dashed and left out of the headline.`
              : ''}
            {gaps > 0
              ? ` ${gaps} minute${gaps === 1 ? '' : 's'} unavailable and shown as gaps.`
              : ''}
          </Text>
        </>
      )}
    </Card>
  );
};
